<script setup lang="ts">
import { FilterButtonsProps, FilterOption } from "@/types/filter.types";
import { computed, ref } from "vue";

const props = withDefaults(defineProps<FilterButtonsProps>(), {
  selectedValues: () => [],
  options: () => [] as FilterOption[],
});

const emit = defineEmits(["change", "update:selectedValues"]);

const selectedOptions: WritableComputedRef<string | string[]> = computed({
  get: () => props.selectedValues,
  set: (newValues) => {
    emit("update:selectedValues", newValues);
    emit("change", newValues);
  },
});

const toggleOption = (optionValue: string) => {
  if (props.radio) {
    selectedOptions.value = optionValue;
  } else if (Array.isArray(selectedOptions.value)) {
    selectedOptions.value = selectedOptions.value.includes(optionValue)
      ? selectedOptions.value.filter((value) => value !== optionValue)
      : [...selectedOptions.value, optionValue];
  }
};

const isSelected = (optionValue: string) => {
  if (props.radio) {
    return selectedOptions.value === optionValue;
  } else {
    return (
      Array.isArray(selectedOptions.value) &&
      selectedOptions.value.includes(optionValue)
    );
  }
};

const handleKeyDown = (event: KeyboardEvent, optionValue: string) => {
  if (event.key === "Enter" || event.key === " ") {
    event.preventDefault();
    toggleOption(optionValue);
  }
};

const buttonRefs = ref<Array<Element | ComponentPublicInstance>>([]);

const optionsLength = computed(() => props.options?.length || 1);

const focusNextButton = (currentIndex: number) => {
  const nextIndex = (currentIndex + 1) % optionsLength.value;
  (buttonRefs.value[nextIndex] as HTMLElement)?.focus();
};

const focusPreviousButton = (currentIndex: number) => {
  const previousIndex =
    (currentIndex - 1 + optionsLength.value) % optionsLength.value;
  (buttonRefs.value[previousIndex] as HTMLElement)?.focus();
};

const handleGroupKeyDown = (event: KeyboardEvent, currentIndex: number) => {
  switch (event.key) {
    case "ArrowRight":
    case "ArrowDown":
      event.preventDefault();
      focusNextButton(currentIndex);
      break;
    case "ArrowLeft":
    case "ArrowUp":
      event.preventDefault();
      focusPreviousButton(currentIndex);
      break;
  }
};
</script>

<template>
  <div class="d-flex flex-wrap gap-2" role="group" aria-label="Filter options">
    <template v-if="options">
      <label
        v-for="(option, index) in options"
        :key="option.id"
        class="filter-button"
        :for="option.id"
      >
        <input
          :id="option.id"
          :ref="
            (el) => {
              if (el) buttonRefs[index] = el;
            }
          "
          :aria-checked="isSelected(option.value.toString())"
          class="form-check-input filter-button__input"
          :type="props.radio ? 'radio' : 'checkbox'"
          :name="option.id"
          :value="option.id"
          :checked="isSelected(option.value.toString())"
          @click="toggleOption(option.value.toString())"
          @keydown="handleKeyDown($event, option.value.toString())"
          @keydown.arrow-left.arrow-right.arrow-up.arrow-down.prevent="
            handleGroupKeyDown($event, index)
          "
        />
        <div class="filter-button__content">
          <i v-if="option.icon" :class="`bi ${option.icon}`"></i>
          {{ option.label }}
        </div>
      </label>
    </template>
  </div>
</template>

<style lang="scss" scoped>
.filter-button {
  position: relative;
  display: inline-flex;
  font-size: $font-size-sm;
  color: $color-black;

  &__content {
    display: flex;
    align-items: center;
    gap: $spacer-2;
    padding: $btn-padding-y $btn-padding-x;
    color: $secondary;
    background-color: $color-white;
    border: 2px solid $border-color;
    border-radius: $border-radius-lg;
    box-shadow: $shadow-brand;
    transition: all 0.1s ease-in-out;

    &:hover {
      background-color: $hover-color;
    }
  }

  &__input {
    position: absolute;
    height: 1px;
    width: 1px;
    overflow: hidden;
    clip: rect(1px, 1px, 1px, 1px);

    &:focus-visible + .filter-button__content {
      border-color: $primary;
      box-shadow:
        $shadow-brand,
        0 0 0 1px $primary;
    }

    &:checked + .filter-button__content {
      border-color: $primary;
    }

    &:checked:focus-visible + .filter-button__content {
      border-color: $primary;
      box-shadow:
        $shadow-brand,
        0 0 0 2px rgba($primary, 0.5);
    }
  }
}
</style>
